import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../lib/api'
import AdminLayout from '../components/AdminLayout'
import {
  Field,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
  FieldLegend,
  FieldSet,
} from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { DatePicker } from '@/components/DatePicker'

type Assignment = {
  id: number
  project_code: string
  project_name: string
  role: string
  vendor_contract_number: string | null
  effective_rate: string
  effective_rate_unit: 'HOUR' | 'DAY'
  effective_rate_per_hour: string
  effective_currency: string
  max_hours: string | null
  hours_approved: string
  hours_logged: string
  start_date: string
  end_date: string | null
  status: string
}

type Entry = {
  id: number
  report: number
  work_date: string
  hours: string
  task: number | null
  task_name: string | null
  description: string
}

type Report = {
  id: number
  assignment: number
  project_code: string
  project_name: string
  employee_name: string
  period_start: string
  period_end: string
  status: 'DRAFT' | 'SUBMITTED' | 'APPROVED' | 'REJECTED'
  total_hours: string
  notes: string
  submitted_at: string | null
  approved_by_username: string | null
  approved_at: string | null
  rejection_reason: string
  generated_bill_number: string | null
  effective_rate: string
  effective_rate_unit: 'HOUR' | 'DAY'
  effective_rate_per_hour: string
  effective_currency: string
  entries: Entry[]
}

const STATUS_BADGE: Record<Report['status'], string> = {
  DRAFT: 'bg-gray-100 text-gray-700',
  SUBMITTED: 'bg-blue-100 text-blue-800',
  APPROVED: 'bg-green-100 text-green-800',
  REJECTED: 'bg-red-100 text-red-700',
}

export default function MyWorkReports() {
  const qc = useQueryClient()

  const { data: assignments = [] } = useQuery({
    queryKey: ['consulting', 'assignments', 'mine'],
    queryFn: async () => (await api.get<Assignment[]>('/consulting/assignments/?mine=1')).data,
  })
  const { data: reports = [], isLoading } = useQuery({
    queryKey: ['consulting', 'work-reports', 'mine'],
    queryFn: async () => (await api.get<Report[]>('/consulting/work-reports/?mine=1')).data,
  })

  const [openReportId, setOpenReportId] = useState<number | null>(null)
  const [creatingFor, setCreatingFor] = useState<Assignment | null>(null)

  const createMut = useMutation({
    mutationFn: async (payload: { assignment: number; period_start: string; period_end: string; notes: string }) =>
      (await api.post('/consulting/work-reports/', payload)).data,
    onSuccess: (created) => {
      qc.invalidateQueries({ queryKey: ['consulting', 'work-reports', 'mine'] })
      setCreatingFor(null)
      setOpenReportId(created.id)
    },
  })

  const activeAssignments = assignments.filter((a) => a.status === 'ACTIVE')
  const openReport = reports.find((r) => r.id === openReportId) || null

  return (
    <AdminLayout>
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-800">Work Report</h2>
        <p className="text-sm text-gray-500 mt-1">
          Log the hours you spent on each project. One report per project per period.
          Submit when complete; your project manager reviews and approves.
        </p>
      </div>

      {/* Assignments overview */}
      <section className="mb-8">
        <h3 className="text-sm font-semibold text-gray-700 mb-3">My active assignments</h3>
        {activeAssignments.length === 0 ? (
          <div className="bg-white rounded-lg border border-gray-200 p-6 text-sm text-gray-500">
            You have no active project assignments. Ask a project manager to add you to a project.
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {activeAssignments.map((a) => (
              <div key={a.id} className="bg-white border border-gray-200 rounded-lg p-4">
                <div className="font-mono text-xs text-gray-500">{a.project_code}</div>
                <div className="font-semibold text-gray-800 mt-0.5">{a.project_name}</div>
                <div className="text-xs text-gray-500 mt-2">
                  Rate: <span className="font-mono">{a.effective_rate} {a.effective_currency} / {a.effective_rate_unit === 'DAY' ? 'day' : 'hour'}</span>
                  {a.vendor_contract_number && (
                    <> · Contract <span className="font-mono">{a.vendor_contract_number}</span></>
                  )}
                </div>
                <div className="text-xs text-gray-500">
                  Approved hours: <span className="font-semibold text-green-700">{a.hours_approved}</span>
                  {a.max_hours && <span> / cap {a.max_hours}</span>}
                </div>
                <Button
                  type="button"
                  size="sm"
                  onClick={() => setCreatingFor(a)}
                  className="mt-3 w-full"
                >
                  + New Work Report
                </Button>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Reports table */}
      <section>
        <h3 className="text-sm font-semibold text-gray-700 mb-3">My reports</h3>
        {isLoading ? (
          <p className="text-sm text-gray-500">Loading…</p>
        ) : reports.length === 0 ? (
          <div className="bg-white rounded-lg border border-gray-200 p-6 text-sm text-gray-500">
            No reports yet. Create one from an assignment above.
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-left">
                <tr>
                  <th className="px-4 py-2 font-semibold text-gray-600">Project</th>
                  <th className="px-4 py-2 font-semibold text-gray-600">Period</th>
                  <th className="px-4 py-2 font-semibold text-gray-600">Hours</th>
                  <th className="px-4 py-2 font-semibold text-gray-600">Status</th>
                  <th className="px-4 py-2 font-semibold text-gray-600">Reviewer</th>
                  <th className="px-4 py-2 text-right" />
                </tr>
              </thead>
              <tbody>
                {reports.map((r) => (
                  <tr key={r.id} className="border-t border-gray-100 hover:bg-gray-50">
                    <td className="px-4 py-2">
                      <div className="font-mono text-xs text-gray-500">{r.project_code}</div>
                      <div>{r.project_name}</div>
                    </td>
                    <td className="px-4 py-2 text-xs text-gray-600">
                      {r.period_start} → {r.period_end}
                    </td>
                    <td className="px-4 py-2 font-semibold">{r.total_hours}h</td>
                    <td className="px-4 py-2">
                      <span className={`text-xs px-2 py-0.5 rounded font-semibold ${STATUS_BADGE[r.status]}`}>
                        {r.status}
                      </span>
                      {r.status === 'REJECTED' && r.rejection_reason && (
                        <div className="text-xs text-red-600 mt-1">"{r.rejection_reason}"</div>
                      )}
                      {r.generated_bill_number && (
                        <div className="text-xs text-gray-500 mt-1">→ {r.generated_bill_number}</div>
                      )}
                    </td>
                    <td className="px-4 py-2 text-xs text-gray-600">
                      {r.approved_by_username || '—'}
                    </td>
                    <td className="px-4 py-2 text-right text-xs">
                      <button onClick={() => setOpenReportId(r.id)} className="text-blue-600 hover:underline">
                        {r.status === 'DRAFT' ? 'Edit' : 'View'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {creatingFor && (
        <NewReportDialog
          assignment={creatingFor}
          onCancel={() => setCreatingFor(null)}
          onCreate={(payload) => createMut.mutate({ ...payload, assignment: creatingFor.id })}
          saving={createMut.isPending}
        />
      )}

      {openReport && (
        <ReportDialog
          report={openReport}
          onClose={() => setOpenReportId(null)}
        />
      )}
    </AdminLayout>
  )
}

// ─────────────────── New Report dialog ───────────────────

function NewReportDialog({
  assignment,
  onCancel,
  onCreate,
  saving,
}: {
  assignment: Assignment
  onCancel: () => void
  onCreate: (p: { period_start: string; period_end: string; notes: string }) => void
  saving: boolean
}) {
  const today = new Date().toISOString().slice(0, 10)
  const startOfMonth = today.slice(0, 7) + '-01'
  const [periodStart, setPeriodStart] = useState(startOfMonth)
  const [periodEnd, setPeriodEnd] = useState(today)
  const [notes, setNotes] = useState('')

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
        <div className="p-5 border-b border-gray-200">
          <h3 className="text-lg font-bold">New Work Report</h3>
          <p className="text-xs text-gray-500 mt-1">
            <span className="font-mono">{assignment.project_code}</span> · {assignment.project_name}
          </p>
        </div>
        <div className="p-5">
          <FieldGroup>
            <Field>
              <FieldLabel htmlFor="nr-period-start">Period start</FieldLabel>
              <DatePicker
                id="nr-period-start" value={periodStart}
                onChange={(v) => setPeriodStart(v)}
              />
            </Field>
            <Field>
              <FieldLabel htmlFor="nr-period-end">Period end</FieldLabel>
              <DatePicker
                id="nr-period-end" value={periodEnd}
                onChange={(v) => setPeriodEnd(v)}
              />
            </Field>
            <Field>
              <FieldLabel htmlFor="nr-notes">Notes (optional)</FieldLabel>
              <Textarea
                id="nr-notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={2}
                placeholder="Optional summary"
              />
            </Field>
          </FieldGroup>
        </div>
        <div className="p-4 border-t border-gray-200 flex justify-end gap-2 bg-gray-50 rounded-b-lg">
          <Button type="button" variant="outline" onClick={onCancel}>Cancel</Button>
          <Button
            type="button"
            onClick={() => onCreate({ period_start: periodStart, period_end: periodEnd, notes })}
            disabled={saving || !periodStart || !periodEnd}
          >
            {saving ? 'Creating…' : 'Create & open'}
          </Button>
        </div>
      </div>
    </div>
  )
}

// ─────────────────── Report dialog (entries editor + submit) ───────────────────

function ReportDialog({ report, onClose }: { report: Report; onClose: () => void }) {
  const qc = useQueryClient()
  const isDraft = report.status === 'DRAFT'

  const [newEntry, setNewEntry] = useState({
    work_date: report.period_start,
    hours: '',
    description: '',
  })

  const addEntryMut = useMutation({
    mutationFn: async () =>
      (await api.post('/consulting/work-report-entries/', {
        report: report.id,
        work_date: newEntry.work_date,
        hours: newEntry.hours,
        description: newEntry.description,
      })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['consulting', 'work-reports', 'mine'] })
      setNewEntry({ work_date: report.period_start, hours: '', description: '' })
    },
  })

  const deleteEntryMut = useMutation({
    mutationFn: async (id: number) => api.delete(`/consulting/work-report-entries/${id}/`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['consulting', 'work-reports', 'mine'] }),
  })

  const submitMut = useMutation({
    mutationFn: async () => (await api.post(`/consulting/work-reports/${report.id}/submit/`)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['consulting', 'work-reports', 'mine'] })
      onClose()
    },
  })

  const reopenMut = useMutation({
    mutationFn: async () => (await api.post(`/consulting/work-reports/${report.id}/reopen/`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['consulting', 'work-reports', 'mine'] }),
  })

  const deleteMut = useMutation({
    mutationFn: async () => api.delete(`/consulting/work-reports/${report.id}/`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['consulting', 'work-reports', 'mine'] })
      onClose()
    },
  })

  const submitError = (submitMut.error as any)?.response?.data?.detail || (submitMut.error as any)?.response?.data?.[0]

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[95vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-gray-200 sticky top-0 bg-white z-10 flex justify-between">
          <div>
            <div className="font-mono text-xs text-gray-500">{report.project_code}</div>
            <h3 className="text-lg font-bold">{report.project_name}</h3>
            <div className="text-xs text-gray-500 mt-0.5">
              {report.period_start} → {report.period_end}
              <span className={`ml-3 text-xs px-2 py-0.5 rounded font-semibold ${STATUS_BADGE[report.status]}`}>
                {report.status}
              </span>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-900 text-xl">×</button>
        </div>

        {report.status === 'REJECTED' && report.rejection_reason && (
          <div className="m-6 p-3 bg-red-50 border border-red-200 rounded text-sm text-red-800">
            <strong>Rejected:</strong> {report.rejection_reason}
          </div>
        )}

        <div className="p-6">
          <h4 className="text-sm font-semibold text-gray-700 mb-3">Daily entries</h4>
          <table className="w-full text-sm mb-4">
            <thead className="text-left text-xs text-gray-500">
              <tr>
                <th className="pb-2">Date</th>
                <th className="pb-2">Hours</th>
                <th className="pb-2">Description</th>
                {isDraft && <th className="pb-2 text-right" />}
              </tr>
            </thead>
            <tbody>
              {report.entries.map((e) => (
                <tr key={e.id} className="border-t border-gray-100">
                  <td className="py-2 text-xs">{e.work_date}</td>
                  <td className="py-2 font-mono">{e.hours}</td>
                  <td className="py-2 text-xs text-gray-700">{e.description || '—'}</td>
                  {isDraft && (
                    <td className="py-2 text-right">
                      <button
                        onClick={() => deleteEntryMut.mutate(e.id)}
                        className="text-xs text-red-600 hover:underline"
                      >
                        Delete
                      </button>
                    </td>
                  )}
                </tr>
              ))}
              {report.entries.length === 0 && (
                <tr><td colSpan={4} className="py-3 text-center text-gray-400 text-xs">No entries yet.</td></tr>
              )}
              <tr className="border-t-2 border-gray-300 font-semibold">
                <td className="py-2 text-xs">Total</td>
                <td className="py-2 font-mono">{report.total_hours}h</td>
                <td className="py-2 text-xs text-gray-500">
                  ≈ {(parseFloat(report.total_hours) * parseFloat(report.effective_rate_per_hour)).toFixed(2)} {report.effective_currency}
                </td>
                <td />
              </tr>
            </tbody>
          </table>

          {isDraft && (
            <div className="bg-gray-50 border border-gray-200 rounded p-3 mb-4">
              <h5 className="text-xs font-semibold text-gray-700 uppercase mb-2">Add entry</h5>
              <div className="grid grid-cols-12 gap-2 items-end">
                <div className="col-span-3">
                  <Field>
                    <FieldLabel htmlFor="entry-date">Date</FieldLabel>
                    <DatePicker
                      id="entry-date"
                      min={report.period_start}
                      max={report.period_end}
                      value={newEntry.work_date}
                      onChange={(v) => setNewEntry({ ...newEntry, work_date: v })}
                    />
                  </Field>
                </div>
                <div className="col-span-2">
                  <Field>
                    <FieldLabel htmlFor="entry-hours">Hours</FieldLabel>
                    <Input
                      id="entry-hours"
                      type="number" step="0.25" min="0.25" max="24"
                      placeholder="hours"
                      value={newEntry.hours}
                      onChange={(e) => setNewEntry({ ...newEntry, hours: e.target.value })}
                    />
                  </Field>
                </div>
                <div className="col-span-5">
                  <Field>
                    <FieldLabel htmlFor="entry-desc">Description</FieldLabel>
                    <Input
                      id="entry-desc"
                      placeholder="What did you work on?"
                      value={newEntry.description}
                      onChange={(e) => setNewEntry({ ...newEntry, description: e.target.value })}
                    />
                  </Field>
                </div>
                <div className="col-span-2">
                  <Button
                    type="button"
                    onClick={() => addEntryMut.mutate()}
                    disabled={addEntryMut.isPending || !newEntry.hours}
                    className="w-full"
                  >
                    + Add
                  </Button>
                </div>
              </div>
            </div>
          )}

          {submitError && (
            <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded text-sm text-red-800">
              {submitError}
            </div>
          )}
        </div>

        <div className="p-4 border-t border-gray-200 flex justify-between gap-2 bg-gray-50 rounded-b-lg sticky bottom-0">
          <div>
            {isDraft && (
              <button
                onClick={() => { if (confirm('Delete this draft report?')) deleteMut.mutate() }}
                className="text-sm text-red-600 hover:underline"
              >
                Delete draft
              </button>
            )}
            {report.status === 'REJECTED' && (
              <button
                onClick={() => reopenMut.mutate()}
                className="text-sm text-blue-600 hover:underline"
              >
                Reopen for edits
              </button>
            )}
          </div>
          <div className="flex gap-2">
            <Button type="button" variant="outline" onClick={onClose}>
              Close
            </Button>
            {isDraft && (
              <Button
                type="button"
                onClick={() => submitMut.mutate()}
                disabled={submitMut.isPending || report.entries.length === 0}
                className="bg-green-600 hover:bg-green-700 text-white"
              >
                {submitMut.isPending ? 'Submitting…' : 'Submit for approval'}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
